<template>
  <div class="fission-chain-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>U235 + n → Ba + Kr + 3n | 临界状态$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation" @click="addNeutron">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Nuclei (U235) -->
          <g v-for="n in nuclei" :key="n.id">
             <circle v-if="!n.fissioned" :cx="n.x" :cy="n.y" r="8" fill="#57606f" stroke="#fff" />
             <!-- Fission remnants -->
             <g v-else>
                <circle :cx="n.x - 5" :cy="n.y - 5" r="4" fill="#ff4757" />
                <circle :cx="n.x + 5" :cy="n.y + 5" r="4" fill="#ffa502" />
                <circle :cx="n.x" :cy="n.y" r="15" fill="rgba(255, 71, 87, 0.2)">
                   <animate attributeName="opacity" values="1;0" dur="0.5s" fill="freeze" />
                </circle>
             </g>
          </g>

          <!-- Free Neutrons -->
          <circle v-for="neu in neutrons" :key="neu.id" :cx="neu.x" :cy="neu.y" r="2" fill="#00d2ff" />

          <text x="10" y="30" fill="#888" font-size="8">点击屏幕释放一个诱发中子</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>增殖系数 k: {{ k_factor }}</label>
              <div class="value" :style="{color: k_factor > 1 ? '#ff4757' : '#2ed573'}">
                 {{ k_factor > 1 ? '超临界 (爆炸)' : '次临界/稳态' }}
              </div>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center">
             <label>控制棒深度 (调节 k)</label>
             <el-slider v-model="moderator" :min="0" :max="100" size="mini"></el-slider>
          </div>
          <el-button type="warning" size="mini" @click="resetSearch" class="mt-2">重新装载燃料</el-button>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>核裂变链式反应是指由重核裂变产生的中子，继续诱发其他重核发生裂变的过程。如果平均每个裂变原子核产生的中子，能引起多于一个（k > 1）其他原子核裂变，反应就会呈指数级加速，释放巨大能量。核电站利用**控制棒**吸收多余中子，使增殖系数 k 维持在 1 左右，从而实现平稳的能量输出。这是原子能和平利用与国防核技术的物理核心。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NuclearFissionChainViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      nuclei: [],
      neutrons: [],
      moderator: 50,
      timer: null,
      idCounter: 0
    }
  },
  computed: {
    k_factor() {
       return (1.2 - (this.moderator / 100) * 0.8).toFixed(2);
    }
  },
  methods: {
    init() {
       this.nuclei = [];
       this.neutrons = [];
       for (let i = 0; i < 40; i++) {
          this.nuclei.push({
             id: i,
             x: 40 + Math.random() * 320,
             y: 40 + Math.random() * 160,
             fissioned: false
          });
       }
    },
    addNeutron() {
       this.neutrons.push({
          id: this.idCounter++,
          x: 0,
          y: 60 + Math.random() * 120,
          vx: 5,
          vy: (Math.random() - 0.5) * 2
       });
    },
    resetSearch() {
       this.init();
    },
    update() {
       this.neutrons.forEach((neu, idx) => {
          neu.x += neu.vx;
          neu.y += neu.vy;
          
          // Collision check
          this.nuclei.forEach(n => {
             if (!n.fissioned && Math.hypot(neu.x - n.x, neu.y - n.y) < 10) {
                n.fissioned = true;
                this.neutrons.splice(idx, 1);
                // Create new neutrons based on k_factor
                let count = this.k_factor > 1 ? 3 : (Math.random() < this.k_factor ? 2 : 1);
                for (let i = 0; i < count; i++) {
                   let angle = Math.random() * Math.PI * 2;
                   this.neutrons.push({
                      id: this.idCounter++,
                      x: n.x,
                      y: n.y,
                      vx: Math.cos(angle) * 4,
                      vy: Math.sin(angle) * 4
                   });
                }
             }
          });

          if (neu.x < 0 || neu.x > 400 || neu.y < 0 || neu.y > 240) {
             this.neutrons.splice(idx, 1);
          }
       });
       this.timer = requestAnimationFrame(this.update);
    }
  },
  mounted() { this.init(); this.update(); },
  beforeDestroy() { cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.fission-chain-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(255, 71, 87, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
